import { Box } from '@ghq-abi/design-system';
import { Button, Card, IconWrapper } from '@ghq-abi/design-system-v2';
import { useTranslate } from '@tolgee/react';
import { useRouter } from 'next/router';
import React, { useCallback, useState } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import { Avatar } from '~/app/components/Avatar';
import { EmployeeInterface } from '~/entities/Employee/dto/employee-details.dto';
import { useEmployeeContext } from '~/entities/Employee/providers/EmployeeContextProvider';
import { OnBehalfDisclaimerDialog } from '~/shared/components/OnBehalfDisclaimerDialog';
// eslint-disable-next-line import/no-cycle
import { EmployeeDetailsProps } from './EmployeeDetails.props';
import { EmployeeSearch } from './EmployeeSearch';
import { FormComponentContainer, FormComponentHeader, FormComponentTitle } from './styles';

export const EmployeeDetails: React.FC<EmployeeDetailsProps> = ({
  isSticky = false,
  onToggleOnBehalf,
  onBehalfCallback,
  apiStep,
  initialExpanded = false,
  mode = 'edit',
}) => {
  const { t } = useTranslate();
  const {
    employee: loggedEmployee,
    isLoading,
    isOnBehalf,
    setIsOnBehalf,
    onBehalfEmployee,
    canSubmitOnBehalf,
    setOnBehalfEmployee,
  } = useEmployeeContext();

  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [showDisclaimerDialog, setShowDisclaimerDialog] = useState(false);
  const router = useRouter();

  const displayEmployee = isOnBehalf ? onBehalfEmployee : loggedEmployee;

  const handleEmployeeSelect = useCallback(
    (employee: EmployeeInterface) => {
      setOnBehalfEmployee(employee);
    },
    [setOnBehalfEmployee]
  );

  const handleClearSearch = useCallback(() => {
    setOnBehalfEmployee(null);
  }, [setOnBehalfEmployee]);

  const handleOnBehalfClick = useCallback(() => {
    if (!isOnBehalf) {
      setShowDisclaimerDialog(true);
      return;
    }

    const newIsOnBehalf = !isOnBehalf;

    setIsOnBehalf(newIsOnBehalf);

    if (onToggleOnBehalf) {
      onToggleOnBehalf();
    }

    if (onBehalfCallback) {
      onBehalfCallback();
    }
  }, [isOnBehalf, setIsOnBehalf, onToggleOnBehalf, onBehalfCallback]);

  const handleConfirmOnBehalf = useCallback(() => {
    setIsOnBehalf(true);

    if (onToggleOnBehalf) {
      onToggleOnBehalf();
    }

    if (onBehalfCallback) {
      onBehalfCallback();
    }
  }, [setIsOnBehalf, onToggleOnBehalf, onBehalfCallback]);

  const handleReturnToPreviousPage = useCallback(() => {
    router.back();
  }, [router]);

  if (isLoading) {
    return (
      <FormComponentContainer
        css={isSticky ? { position: 'sticky', top: 0, zIndex: 100 } : {}}
        id="step-1"
      >
        <FormComponentHeader>
          <FormComponentTitle>
            {t('form.employeeDetails.title')}
          </FormComponentTitle>
        </FormComponentHeader>
        <Box css={{ padding: '$6', textAlign: 'center', color: '$gray600' }}>
          {t('form.employeeDetails.loadingEmployee')}
        </Box>
      </FormComponentContainer>
    );
  }

  if (!loggedEmployee && !isOnBehalf) return null;

  const fallback = (value?: string | null) =>
    value?.trim() || t('form.employeeDetails.notAvailable');

  const getFieldLabel = (fieldKey: string, fallbackLabel: string) => {
    if (apiStep) {
      const question = apiStep.questions.find(q =>
        q.questionTranslationKey.includes(fieldKey)
      );
      if (question) {
        return t(question.inputLabelTranslationKey, fallbackLabel);
      }
    }
    switch (fieldKey) {
      case 'employeeName':
        return t('form.employeeDetails.employeeName');
      case 'positionTitle':
        return t('form.employeeDetails.positionTitle');
      case 'employeeId':
        return t('form.employeeDetails.employeeId');
      case 'managerName':
        return t('form.employeeDetails.managerName');
      case 'employeeEmail':
        return t('form.employeeDetails.employeeEmail');
      case 'zone':
        return t('form.employeeDetails.zone');
      case 'employeeBand':
        return t('form.employeeDetails.employeeBand');
      case 'countryBU':
        return t('form.employeeDetails.bu');
      case 'departmentFunction':
        return t('form.employeeDetails.departmentFunction');
      case 'department':
        return t('form.employeeDetails.department');
      default:
        return fallbackLabel;
    }
  };

  const details = displayEmployee
    ? [
      ['employeeName', 'Employee Name', fallback(displayEmployee.name)],
      ['positionTitle', 'Position Title', fallback(displayEmployee.position)],
      ['employeeId', 'Employee ID', fallback(displayEmployee.id)],
      ['managerName', 'Manager Name', fallback(displayEmployee.manager)],
      ['employeeEmail', 'Employee Email', fallback(displayEmployee.email)],
      ['zone', 'Zone', fallback(displayEmployee.zone)],
      ['employeeBand', 'Employee Band', fallback(displayEmployee.band)],
      ['countryBU', 'BU', fallback(displayEmployee.country)],
      ['departmentFunction', 'Department', fallback(displayEmployee.departmentFunction)],
      ['department', 'Office', fallback(displayEmployee.department)],
    ].filter(([fieldKey]) => !(isOnBehalf && fieldKey === 'employeeBand'))
    : [];

  const shouldShowEmployeeInfo = displayEmployee && (!isOnBehalf || onBehalfEmployee);

  const shouldShowSearch = isOnBehalf && !onBehalfEmployee;

  return (
    <Card.Root
      id="step-1"
      className={`
    border border-gray-200 bg-white rounded-[4px] mb-2
    ${isSticky ? 'sticky top-0 z-[100]' : ''}
  `}
    >
      <Card.Header className="flex flex-row justify-between items-center border-b min-h-[52px]  border-gray-200 px-6 pt-2 pb-2 bg-[#F9FAFB] w-full rounded-t-[4px]">
        <Card.Title className="text-md font-semibold text-gray-800 m-0 p-0">
          {t('form.employeeDetails.title')}
        </Card.Title>

        {mode === 'edit' && canSubmitOnBehalf && (
          <Button
            variant="secondary"
            border="default"
            size="md"
            round="minimal"
            className={`
              h-8 font-medium text-[13px] whitespace-nowrap min-w-[136px] p-1 cursor-pointer
              ${isOnBehalf
                ? 'bg-[#F2DC39] text-[#191F2E] hover:bg-[#f2dc39]/90 border-[#F2DC39]'
                : ''
              }
            `}
            onClick={handleOnBehalfClick}
          >
            {isOnBehalf
              ? t('form.employeeDetails.raiseForYourself')
              : t('form.employeeDetails.raiseOnBehalf')}
          </Button>
        )}
      </Card.Header>

      <Card.Content className="p-0 sm:p-6">
        {shouldShowEmployeeInfo && (
          <Box className="p-1">
            <Box
              onClick={() => setIsExpanded(!isExpanded)}
              className={`
                flex items-center cursor-pointer relative
                p-6 pr-16 min-h-[64px]
                transition-colors duration-200
                hover:bg-gray-50
              `}
            >
              <Avatar
                employeeId={displayEmployee.id}
                name={displayEmployee.name}
                globalId={displayEmployee.id}
                size='lg'
              />

              <Box className="flex flex-col ml-3 flex-1">
                <span className="text-sm font-medium text-gray-900">
                  {fallback(displayEmployee.id)}{' '}
                  {fallback(displayEmployee.name)}
                </span>
                <span className="text-xs text-gray-600 mt-1">
                  {fallback(displayEmployee.email)}
                </span>
                {isOnBehalf && onBehalfEmployee && (
                  <Box className="text-xs text-[#191F2E] px-2 py-1 bg-[#F2DC39] rounded font-medium mt-2 mr-auto">
                    {t('form.employeeDetails.onBehalfOf')}
                  </Box>
                )}
              </Box>

              <Button
                type="button"
                aria-expanded={isExpanded}
                className="absolute right-4 w-10 h-10 flex items-center justify-center bg-white rounded"
                variant="secondary"
                round="none"
              >
                <IconWrapper variant="default" size={40}>
                  <HiChevronDown
                    size={32}
                    color="#6B7280"
                    style={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                </IconWrapper>
              </Button>
            </Box>

            {isExpanded && (
              <Box className="grid grid-cols-1 gap-2 mt-0 px-7 py-4 md:grid-cols-2 md:gap-x-8 md:gap-y-4 lg:grid-cols-4 lg:gap-x-8 lg:gap-y-4">
                {details.map(([fieldKey, fallbackLabel, value]) => (
                  <Box
                    key={fieldKey as string}
                    className="flex flex-col items-start mb-0 gap-[2px]"
                  >
                    <span className="text-sm font-semibold leading-[1.6] tracking-[0] text-gray-900">
                      {getFieldLabel(
                        fieldKey as string,
                        fallbackLabel as string
                      )}
                    </span>
                    <span className="text-sm font-semibold leading-[1.6] tracking-[0] text-gray-500">
                      {value as string}
                    </span>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
        {shouldShowSearch && (
          <Box className="p-6 bg-white rounded-b-[4px]">
            <EmployeeSearch
              onEmployeeSelect={handleEmployeeSelect}
              onClear={handleClearSearch}
            />
          </Box>
        )}
      </Card.Content>

      <OnBehalfDisclaimerDialog
        isOpen={showDisclaimerDialog}
        onClose={() => setShowDisclaimerDialog(false)}
        onConfirm={handleConfirmOnBehalf}
        onReturn={handleReturnToPreviousPage}
      />
    </Card.Root>
  );
};
